import React, { useState, useEffect } from 'react';
import { Search } from 'lucide-react';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet icons in React
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

let DefaultIcon = L.icon({
    iconUrl: icon,
    shadowUrl: iconShadow,
    iconAnchor: [12, 41]
});

L.Marker.prototype.options.icon = DefaultIcon;

interface MapPickerProps {
    onLocationSelect: (location: string, lat: number, lng: number) => void;
    initialLocation?: string;
}

const MapEvents: React.FC<{ onSelect: (lat: number, lng: number) => void }> = ({ onSelect }) => {
    useMapEvents({
        click(e) {
            onSelect(e.latlng.lat, e.latlng.lng);
        },
    });
    return null;
};

const MapUpdater: React.FC<{ center: [number, number] }> = ({ center }) => {
    const map = useMap();
    useEffect(() => {
        map.flyTo(center, 13);
    }, [center, map]);
    return null;
};

export const MapPicker: React.FC<MapPickerProps> = ({ onLocationSelect, initialLocation }) => {
    const [searchQuery, setSearchQuery] = useState(initialLocation || '');
    const [position, setPosition] = useState<[number, number] | null>(null);
    const [loading, setLoading] = useState(false);
    const [mapType, setMapType] = useState<'satellite' | 'street'>('satellite');

    const defaultCenter: [number, number] = [24.7136, 46.6753];

    const handleSearch = async () => {
        if (!searchQuery) return;
        setLoading(true);
        try {
            const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}`);
            const data = await response.json();
            if (data && data.length > 0) {
                const { lat, lon, display_name } = data[0];
                const newPos: [number, number] = [parseFloat(lat), parseFloat(lon)];
                setPosition(newPos);
                onLocationSelect(display_name, newPos[0], newPos[1]);
            }
        } catch (error) {
            console.error("Geocoding error:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleMapClick = async (lat: number, lng: number) => {
        setPosition([lat, lng]);
        setLoading(true);
        try {
            const response = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`);
            const data = await response.json();
            if (data && data.display_name) {
                setSearchQuery(data.display_name);
                onLocationSelect(data.display_name, lat, lng);
            } else {
                setSearchQuery(`${lat.toFixed(4)}, ${lng.toFixed(4)}`);
                onLocationSelect(`${lat.toFixed(4)}, ${lng.toFixed(4)}`, lat, lng);
            }
        } catch (error) {
            console.error("Reverse geocoding error:", error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="space-y-2">
            <div className="flex gap-2">
                <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    placeholder="Search city or place..."
                    className="flex-1 bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-100 focus:outline-none focus:border-indigo-500 placeholder:text-zinc-600"
                />
                <button
                    onClick={handleSearch}
                    disabled={loading}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white p-2 rounded-lg transition-colors"
                >
                    <Search size={16} />
                </button>
            </div>

            <div className="h-48 w-full rounded-lg overflow-hidden border border-zinc-700 relative z-0 group">
                <div className="absolute top-2 right-2 z-[1000] flex bg-zinc-900/80 backdrop-blur-md rounded-lg overflow-hidden border border-zinc-700 shadow-xl">
                    <button
                        onClick={() => setMapType('street')}
                        className={`px-3 py-1 text-[10px] font-bold uppercase tracking-wider transition-all ${mapType === 'street' ? 'bg-indigo-600 text-white' : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800'}`}
                    >
                        Street
                    </button>
                    <button
                        onClick={() => setMapType('satellite')}
                        className={`px-3 py-1 text-[10px] font-bold uppercase tracking-wider transition-all ${mapType === 'satellite' ? 'bg-indigo-600 text-white' : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800'}`}
                    >
                        Satellite
                    </button>
                </div>

                <MapContainer center={position || defaultCenter} zoom={13} style={{ height: '100%', width: '100%' }}>
                    <TileLayer
                        attribution={mapType === 'street'
                            ? '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                            : 'Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community'
                        }
                        url={mapType === 'street'
                            ? "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            : "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                        }
                    />
                    {position && (
                        <>
                            <Marker position={position} />
                            <MapUpdater center={position} />
                        </>
                    )}
                    <MapEvents onSelect={handleMapClick} />
                </MapContainer>
            </div>
        </div>
    );
};
